import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { snakeCase } from 'lodash';
import React, { FC } from 'react';
import { CustomFieldProps, FieldStatus, FieldType } from './common';
import { SelectableField } from './SelectableField';

import styles from './MergePair.module.scss';

interface Props {
  fields: { [key: string]: FieldStatus };
  customFields: { [key: string]: FieldStatus };
  changeField: (fieldName: string, status: FieldStatus) => void;
  changeCustomField?: (fieldName: string, status: FieldStatus) => void;
  getFirstPatientValue: (fieldName: string) => string | undefined;
  getSecondPatientValue: (fieldName: string) => string | undefined;
  getFirstPatientCustomValue?: (fieldName: string) => CustomFieldProps;
  getSecondPatientCustomValue?: (fieldName: string) => CustomFieldProps;
  selectAll?: (status: FieldStatus) => void;
}

export const MergePatientsFieldsTable: FC<Props> = (props) => {
  const i18next = useScopedTranslation('merge_assistant.merge_pair');
  const {
    fields,
    customFields,
    changeField,
    changeCustomField,
    getFirstPatientValue,
    getSecondPatientValue,
    getFirstPatientCustomValue,
    getSecondPatientCustomValue,
    selectAll,
  } = props;

  return (
    <div className={styles.valuesContainer}>
      <div className={styles.row}>
        <div className={styles.fieldTitle}></div>
        {selectAll && (
          <>
            <div className={styles.selectAllContainer}>
              <UniLink
                color={ButtonColor.Primary}
                title={i18next.t('select_all')}
                onClick={() => selectAll(FieldStatus.firstPatient)}
              />
            </div>
            <div />
            <div className={styles.selectAllContainer}>
              <UniLink
                color={ButtonColor.Primary}
                title={i18next.t('select_all')}
                onClick={() => selectAll(FieldStatus.secondPatient)}
              />
            </div>
          </>
        )}
      </div>
      {Object.keys(fields)
        .filter((key) => fields[key] !== FieldStatus.const)
        .map((key) => {
          return (
            <div key={key} className={styles.row} data-test-id="merge-field">
              <div
                className={styles.fieldTitle}
                title={i18next.t(`fields.${snakeCase(key)}` as any) as string}
                data-test-id="merge-field-title"
              >
                {i18next.t(`fields.${snakeCase(key)}` as any, {
                  defaultValue: key,
                })}
              </div>
              <SelectableField
                fieldName={key}
                isSelected={fields[key] === FieldStatus.firstPatient}
                onClick={() => changeField(key, FieldStatus.firstPatient)}
                className={styles.field}
                fieldType={FieldType.property}
                value={getFirstPatientValue(key)}
              />
              <div />
              <SelectableField
                fieldName={key}
                isSelected={fields[key] === FieldStatus.secondPatient}
                onClick={() => changeField(key, FieldStatus.secondPatient)}
                className={styles.field}
                fieldType={FieldType.property}
                value={getSecondPatientValue(key)}
              />
            </div>
          );
        })}
      {getFirstPatientCustomValue &&
        getSecondPatientCustomValue &&
        Object.keys(customFields)
          .filter((key) => customFields[key] !== FieldStatus.const)
          .map((key) => {
            return (
              <div key={key} className={styles.row}>
                <div className={styles.fieldTitle} title={key}>
                  {key}
                </div>
                <SelectableField
                  fieldName={key}
                  isSelected={customFields[key] === FieldStatus.firstPatient}
                  onClick={() =>
                    changeCustomField?.(key, FieldStatus.firstPatient)
                  }
                  className={styles.field}
                  fieldType={FieldType.customField}
                  {...getFirstPatientCustomValue(key)}
                />
                <div />
                <SelectableField
                  fieldName={key}
                  isSelected={customFields[key] === FieldStatus.secondPatient}
                  onClick={() =>
                    changeCustomField?.(key, FieldStatus.secondPatient)
                  }
                  className={styles.field}
                  fieldType={FieldType.customField}
                  {...getSecondPatientCustomValue(key)}
                />
              </div>
            );
          })}
    </div>
  );
};
